/**
 * useSettingsForm Hook
 *
 * Custom hook for managing settings forms with auto-save.
 * Uses saveSettings for persistence on blur and unmount.
 *
 * Persistence model:
 *   handleChange → optimistic UI update + mark dirty
 *   handleBlur   → save dirty fields to Firestore
 *   unmount      → save remaining dirty fields
 */

import { useState, useEffect, useCallback, useRef } from "react";
import { saveSettings } from "../services/settingsService";
import { subscribeToSettings } from "../services/settingsService";
import type { SettingsCategory, SettingsData } from "../types/settings";
import type { AppError } from "../types/errors";
import { createLogger } from "../utils/logger";

const formLogger = createLogger({ prefix: "useSettingsForm" });

export interface UseSettingsFormResult<T extends SettingsData> {
  formData: Partial<T>;
  handleChange: (field: keyof T, value: unknown) => void;
  handleBlur: (field: keyof T, value: unknown) => void;
  loading: boolean;
  error: AppError | null;
  retry: () => void;
}

/**
 * Custom hook for managing settings forms with batched auto-save.
 *
 * Features:
 * - Loads settings from Firestore via real-time subscription
 * - handleChange updates UI optimistically (no network call)
 * - handleBlur saves pending changes to Firestore
 * - On unmount, remaining dirty fields are saved
 *
 * @param category - Settings category
 * @param userId - User ID
 * @returns Form state and handlers
 */
export function useSettingsForm<T extends SettingsData = SettingsData>(
  category: SettingsCategory,
  userId: string
): UseSettingsFormResult<T> {
  const [formData, setFormData] = useState<Partial<T>>({});
  const [originalData, setOriginalData] = useState<Partial<T>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<AppError | null>(null);

  const pendingChangesRef = useRef<Partial<T>>({});
  const isMountedRef = useRef(true);

  // Stable refs for userId/category so the unmount cleanup can
  // access the latest values without adding them to the effect deps.
  const userIdRef = useRef(userId);
  const categoryRef = useRef(category);
  useEffect(() => {
    userIdRef.current = userId;
    categoryRef.current = category;
  }, [userId, category]);

  /**
   * Subscribe to real-time settings updates via Firestore onSnapshot.
   */
  useEffect(() => {
    isMountedRef.current = true;

    formLogger.debug("Subscribing to settings", { userId, category });

    const unsubscribe = subscribeToSettings<T>(userId, category, (data) => {
      if (!isMountedRef.current) return;

      const newData = data || ({} as Partial<T>);
      formLogger.debug("Settings snapshot received", { userId, category });

      // Only update form data if there are no pending local changes.
      if (Object.keys(pendingChangesRef.current).length === 0) {
        setFormData(newData);
      }
      setOriginalData(newData);
      setError(null);
      setLoading(false);
    });

    return () => {
      isMountedRef.current = false;
      unsubscribe();

      // Save any remaining dirty changes before unmount
      const pending = pendingChangesRef.current;
      if (Object.keys(pending).length > 0) {
        saveSettings(
          userIdRef.current,
          categoryRef.current,
          pending as Partial<SettingsData>
        ).catch((err) => {
          formLogger.error("Failed to save on unmount", { error: err });
        });
        pendingChangesRef.current = {} as Partial<T>;
      }
    };
  }, [userId, category]);

  /**
   * Handle field change — optimistic UI update only.
   * The value is tracked in pendingChangesRef and will be
   * saved to Firestore when handleBlur fires.
   */
  const handleChange = useCallback((field: keyof T, value: unknown) => {
    formLogger.debug("Field changed", { field, value });
    setFormData((prev) => ({ ...prev, [field]: value }));
    pendingChangesRef.current = {
      ...pendingChangesRef.current,
      [field]: value,
    } as Partial<T>;
  }, []);

  /**
   * Handle field blur — save all pending changes to Firestore.
   *
   * If the blurred field's value is unchanged from originalData, it is
   * removed from pending (no unnecessary write). Then all accumulated
   * pending changes are enqueued.
   */
  const handleBlur = useCallback(
    (field: keyof T, value: unknown) => {
      formLogger.debug("Field blurred", { field, value });

      // Drop unchanged field from pending
      if (originalData[field] === value) {
        formLogger.debug("Value unchanged, removing from pending", {
          field,
          value,
        });
        delete pendingChangesRef.current[field];
      }

      // Enqueue all pending changes
      const pending = pendingChangesRef.current;
      if (Object.keys(pending).length > 0) {
        formLogger.debug("Saving pending changes", {
          userId,
          category,
          fields: Object.keys(pending),
        });
        saveSettings(userId, category, pending as Partial<SettingsData>).catch(
          (err) => {
            formLogger.error("Failed to save on blur", { error: err });
          }
        );
        // Update originalData to reflect saved values
        setOriginalData((prev) => ({ ...prev, ...pending }));
        pendingChangesRef.current = {} as Partial<T>;
        setError(null);
      }
    },
    [originalData, userId, category]
  );

  /**
   * Retry — re-enqueue any fields that differ from originalData.
   */
  const retry = useCallback(() => {
    formLogger.debug("Retrying save", { userId, category });

    const changes: Partial<T> = {};
    Object.keys(formData).forEach((key) => {
      const field = key as keyof T;
      if (formData[field] !== originalData[field]) {
        changes[field] = formData[field];
      }
    });

    if (Object.keys(changes).length > 0) {
      saveSettings(userId, category, changes as Partial<SettingsData>).catch(
        (err) => {
          formLogger.error("Failed to save on retry", { error: err });
        }
      );
      setOriginalData((prev) => ({ ...prev, ...changes }));
      setError(null);
    } else {
      setError(null);
    }
  }, [formData, originalData, userId, category]);

  return {
    formData,
    handleChange,
    handleBlur,
    loading,
    error,
    retry,
  };
}
